/**
 * Verwaltung: alle Partien, unabhängig von Eigentümerschaft.
 *
 * Nur für Inhaber – siehe `RequireOwner` in `App.tsx`. Derselbe Aufbau wie
 * `Admin.tsx` (eigene Kopfzeile statt Seitenleiste, `DataTable`), damit sich
 * die Verwaltung wie ein zusammenhängender Bereich anfühlt.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, type Column, DataTable, Input, Notice, StatusDot } from '../components/ui';
import { api, type AdminGameListEntry } from '../lib/api';
import { useTheme } from '../store/auth';
import { money, num, sinceDate } from '../lib/format';

export function AdminGames() {
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();

  const [games, setGames] = useState<AdminGameListEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (query: string) => {
    try {
      const result = await api.adminListGames(query || undefined, 100);
      setGames(result.games);
      setTotal(result.total);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    // Beim Tippen nicht bei jedem Zeichen fragen – kurze Verzögerung reicht,
    // damit die Liste flüssig bleibt, ohne die Serverlast zu treiben.
    const handle = setTimeout(() => void load(search), 250);
    return () => clearTimeout(handle);
  }, [search, load]);

  const columns: Column<AdminGameListEntry>[] = [
    {
      key: 'name',
      header: 'Partie',
      sortValue: (g) => g.name,
      render: (g) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{g.companyName}</p>
          <p className="truncate text-2xs text-panel-500 dark:text-panel-400">{g.name}</p>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Eigentümer',
      sortValue: (g) => g.ownerDisplayName,
      render: (g) => (
        <div className="min-w-0">
          <p className="truncate">{g.ownerDisplayName}</p>
          <p className="truncate text-2xs text-panel-500 dark:text-panel-400">{g.ownerEmail}</p>
        </div>
      ),
    },
    {
      key: 'running',
      header: 'Zustand',
      hideOnMobile: true,
      sortValue: (g) => (g.running ? 1 : 0),
      render: (g) => (g.running ? <StatusDot tone="success" label={`${g.speed}×`} /> : <StatusDot tone="neutral" label="Pausiert" />),
    },
    {
      key: 'cash',
      header: 'Kasse',
      align: 'right',
      sortValue: (g) => g.cash ?? -Infinity,
      render: (g) => (g.cash === null ? '–' : money(g.cash, true)),
    },
    {
      key: 'customers',
      header: 'Kunden',
      align: 'right',
      hideOnMobile: true,
      sortValue: (g) => g.customers ?? -1,
      render: (g) => (g.customers === null ? '–' : num(g.customers)),
    },
    {
      key: 'companyValue',
      header: 'Unternehmenswert',
      align: 'right',
      hideOnMobile: true,
      sortValue: (g) => g.companyValue ?? -Infinity,
      render: (g) => (g.companyValue === null ? '–' : money(g.companyValue, true)),
    },
    {
      key: 'updatedAt',
      header: 'Zuletzt aktiv',
      align: 'right',
      sortValue: (g) => g.updatedAt,
      render: (g) => <span className="text-xs text-panel-500 dark:text-panel-400">{sinceDate(g.updatedAt)}</span>,
    },
  ];

  return (
    <div className="min-h-full">
      <header className="border-b divider">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            ← Verwaltung
          </Button>
          <button
            type="button"
            onClick={toggle}
            aria-label="Farbschema wechseln"
            className="rounded-md px-2 py-1.5 text-sm text-panel-500 transition-colors hover:bg-panel-200/70 dark:text-panel-400 dark:hover:bg-panel-800"
          >
            {dark ? '☀' : '☾'}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold">Partien</h1>
            <p className="mt-0.5 text-sm text-panel-500 dark:text-panel-400">
              {total} {total === 1 ? 'Partie' : 'Partien'} insgesamt, unabhängig vom Eigentümer.
            </p>
          </div>
          <Input placeholder="Firma, Partie oder E-Mail suchen …" value={search} onChange={(e) => setSearch(e.target.value)} className="w-72" />
        </div>

        {error && (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        )}

        <Card padded={false}>
          <DataTable
            columns={columns}
            rows={games ?? []}
            rowKey={(g) => g.id}
            onRowClick={(g) => navigate(`/admin/games/${g.id}`)}
            empty={games === null ? 'Wird geladen …' : search ? 'Keine Partie passt zur Suche.' : 'Keine Partien vorhanden.'}
          />
        </Card>
      </div>
    </div>
  );
}
